import { BATTLE_LOOT_PERCENT, COLONY_SHIP_ID, DEFAULT_BATTLE_ROUNDS, DEFAULT_SCIENCES, FLEET_MISSION_DEFINITIONS, isTeamPlanet, PIRATE_TRADE_TOKEN_CHANCE, RESOURCE_NAMES, SUN_DEFENCE_RETURN_HOLD_SECONDS, } from "@kaeliux/shared";
import { QueueError } from "./tickEvents.js";
import { resolveBattleSequential } from "./battleSequential.js";
import { calculateFleetCargoCapacity, calculateRecallReturnSeconds } from "./flightEngine.js";
import { buildSpyReportFields, pickSpyReportTier } from "./espionage.js";
import { calculateDockedPopulation, totalShips } from "./ships.js";
import { calculatePirateTokenLootThreshold } from "./trade.js";
import { teamPlanetDockShips, teamPlanetReleaseInFlight, teamPlanetReturnShips } from "./teamPlanetEngine.js";
/**
 * Pure fleet arrival/return resolution — the mission-effect half of the
 * flight system (issues #67/#68). A fleet has two resolution points:
 * arrival at the target (`arrivesAt`) and landing back home (`returnsAt`).
 * Both are resolved lazily by the functions layer whenever a fleet endpoint
 * runs (no cron/polling — the same replay philosophy as
 * `replayEventsToNow`), which:
 *
 *   1. locates + replays the affected planet (target for arrival, origin
 *      for return) via its normal advance pipeline,
 *   2. calls {@link resolveFleetArrival} / {@link resolveFleetReturn} here,
 *   3. commits the returned planet/fleet, and executes the side-channel
 *      instructions (`foundColony`, `spyReport`) this module cannot perform
 *      itself — creating documents is I/O, and this package has none.
 *
 * Attack/pirate arrivals resolve real combat in {@link resolveBattleMission}
 * below (#18/#19) — survivors, plunder and the debris field are all applied
 * here; the functions layer only persists the resulting `BattleReport`. The
 * `battlePending` effect is legacy, marking pre-#18 records only, and is no
 * longer produced (see `FleetEffectType` in shared). `sunDefence`/`sunAttack`
 * are the two missions this module never actually resolves — the functions
 * layer routes them to `processSunArrival` (`apps/functions/src/suns.ts`)
 * before ever calling {@link resolveFleetArrival}, since a Sun's defender
 * roster and a pooled battle's per-fleet results don't fit this function's
 * single-`targetPlanet` contract; the case below is a defensive fallback
 * only. Transport/Station to a Team planet, by contrast, need NO special
 * casing at all — a Team planet is a `Planet` (`Planet.allianceId`), so
 * {@link resolveTransport}/{@link resolveStation} already handle it via the
 * normal `ctx.targetPlanet`/`ctx.targetIsOwn` path.
 */
// ---------------------------------------------------------------------------
// Cargo / ship transfer helpers
// ---------------------------------------------------------------------------
export const EMPTY_CARGO = { metal: 0, minerals: 0, gas: 0, scrap: 0 };
function isEmptyCargo(cargo) {
    return cargo.metal === 0 && cargo.minerals === 0 && cargo.gas === 0 && cargo.scrap === 0;
}
/**
 * Unload cargo into a planet's storage. Metal/minerals/gas are capped at
 * storage capacity — what doesn't fit stays on board (`leftover`) and
 * travels home with the fleet. Scrap has no storage building/capacity, so
 * it always transfers in full. A Team planet is a `Planet` (see
 * `Planet.allianceId`'s doc comment), so this applies to one unmodified —
 * no Team-planet-specific variant needed.
 */
export function deliverCargoToPlanet(planet, cargo) {
    const delivered = { ...EMPTY_CARGO };
    const leftover = { ...EMPTY_CARGO };
    const resources = { ...planet.resources };
    for (const key of ["metal", "minerals", "gas"]) {
        const amount = cargo[key];
        if (amount <= 0)
            continue;
        const state = resources[key];
        const room = Math.max(0, state.capacity - state.current);
        const fits = Math.min(amount, room);
        delivered[key] = fits;
        leftover[key] = amount - fits;
        resources[key] = { ...state, current: state.current + fits };
    }
    delivered.scrap = cargo.scrap;
    if (cargo.scrap > 0) {
        resources.scrap = { current: resources.scrap.current + cargo.scrap };
    }
    return { planet: { ...planet, resources }, delivered, leftover };
}
/** Dock a fleet's ships onto a planet. Overpopulation is handled by the existing planet lock, not here. */
export function dockFleetShips(planet, ships) {
    const docked = { ...planet.ships };
    for (const [key, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const shipId = Number(key);
        docked[shipId] = (docked[shipId] ?? 0) + count;
    }
    return { ...planet, ships: docked };
}
/**
 * Release ships from a planet's `shipsInFlight` ledger (fleet landed, or
 * its ships were consumed/transferred at the target). Clamped at 0 so
 * fleets dispatched before the ledger existed can still land.
 */
export function releaseShipsInFlight(planet, ships) {
    const inFlight = { ...planet.shipsInFlight };
    for (const [key, count] of Object.entries(ships)) {
        if (!count)
            continue;
        const shipId = Number(key);
        // Zero, never delete — planets are committed with a merging write,
        // which deep-merges maps and would resurrect a deleted key's old value.
        inFlight[shipId] = Math.max(0, (inFlight[shipId] ?? 0) - count);
    }
    return { ...planet, shipsInFlight: inFlight };
}
/**
 * Release ships that will never fly home (destroyed in battle, transferred,
 * consumed) from the ledger of the planet `fleet` departed from: a Team
 * planet's dispatching-race ledger when the fleet carries an `originRace`,
 * the flat `shipsInFlight` map otherwise. Until released they still count
 * as that planet's used population (issue #294).
 */
export function releaseOriginShipsInFlight(origin, fleet, ships) {
    return isTeamPlanet(origin) && fleet.originRace
        ? teamPlanetReleaseInFlight(origin, fleet.originRace, ships)
        : releaseShipsInFlight(origin, ships);
}
/**
 * Population of every Station flight still on its way to `target` — shown
 * beside a planet's used population as "(+N)" until the fleets dock (issue
 * #294). A Station that arrives either completes (its ships are now docked
 * and counted normally) or flies home as `returning`, so only `outbound`
 * flights count. A Team planet fleet's ships are priced by its own
 * `originRace`, the dispatcher's race otherwise.
 */
export function calculateIncomingStationPopulation(race, fleets, target) {
    return (fleets ?? [])
        .filter((f) => f.mission === "station" &&
        f.status === "outbound" &&
        f.target.galaxy === target.galaxy &&
        f.target.system === target.system &&
        f.target.position === target.position)
        .reduce((sum, f) => sum + calculateDockedPopulation(f.originRace ?? race, f.ships), 0);
}
/** `before − after` per ship type — the units an arrival consumed/transferred, which will never fly home. */
export function shipManifestDiff(before, after) {
    const diff = {};
    for (const [key, count] of Object.entries(before)) {
        if (!count)
            continue;
        const shipId = Number(key);
        const gone = count - (after[shipId] ?? 0);
        if (gone > 0)
            diff[shipId] = gone;
    }
    return diff;
}
/**
 * Resolve a fleet reaching its target. Dispatch-time validation is NOT
 * repeated here — this function trusts the fleet document (the server
 * created it) and only re-checks facts that can change mid-flight (target
 * became occupied / changed owner).
 */
export function resolveFleetArrival(fleet, ctx) {
    if (fleet.status !== "outbound") {
        throw new QueueError("failed-precondition", `Fleet ${fleet.id} is not outbound`);
    }
    const resolution = dispatchArrival(fleet, ctx);
    const resolved = resolution.fleet;
    // A fleet completes at the target exactly when nothing is left to fly
    // home: a successful Station transfers every ship, a lone colony ship
    // is consumed founding the colony. Checking the ships (not the
    // mission's hasReturnLeg flag) keeps failure fallbacks safe — a
    // Station whose target changed hands mid-flight still has its ships
    // and MUST return, never complete-and-vanish.
    const status = totalShips(resolved.ships) === 0 ? "completed" : "returning";
    return {
        ...resolution,
        // Whatever left the manifest at the target is never coming home —
        // the caller releases it from the origin's shipsInFlight ledger.
        consumedFromOrigin: shipManifestDiff(fleet.ships, resolved.ships),
        fleet: {
            ...resolved,
            status,
            arrivalReport: resolution.report,
            updatedAt: ctx.now,
        },
    };
}
function dispatchArrival(fleet, ctx) {
    switch (fleet.mission) {
        case "transport":
            return resolveTransport(fleet, ctx);
        case "station":
            return resolveStation(fleet, ctx);
        case "colonize":
            return resolveColonize(fleet, ctx);
        case "recycle":
            return resolveRecycle(fleet, ctx);
        case "harvestAsteroid":
            return resolveHarvestAsteroid(fleet, ctx);
        case "espionage":
            return resolveEspionage(fleet, ctx);
        case "attack":
        case "pirate":
            return resolveBattleMission(fleet, ctx);
        case "spaceTrip":
            return noEffect(fleet, ctx, "held", "The fleet waited out its space trip.");
        case "sunDefence":
        case "sunAttack":
        case "sunGarrison":
            // Defensive fallback only: the functions layer routes every Sun
            // mission to `processSunArrival` (`apps/functions/src/suns.ts`)
            // BEFORE ever calling `resolveFleetArrival` — a Sun's defenders
            // and gauntlet battle results don't fit this function's
            // single-`targetPlanet` contract. Reaching this case means that
            // routing was skipped; treat it as a no-op rather than throwing,
            // same as every other "target changed under us" fallback here.
            return noEffect(fleet, ctx, "none", "The fleet returned without engaging.");
    }
}
function noEffect(fleet, ctx, effect, message) {
    return {
        fleet,
        targetPlanet: ctx.targetPlanet,
        report: { effect, message },
    };
}
/** Transport: unload as much cargo as the target's storage takes; the rest rides home. */
function resolveTransport(fleet, ctx) {
    if (!ctx.targetPlanet) {
        return noEffect(fleet, ctx, "none", "No planet at the target coordinates — cargo returns home.");
    }
    const { planet, delivered, leftover } = deliverCargoToPlanet(ctx.targetPlanet, fleet.cargo);
    return {
        fleet: { ...fleet, cargo: leftover },
        targetPlanet: planet,
        report: {
            effect: "delivered",
            resources: delivered,
            message: `Delivered ${delivered.metal} ${RESOURCE_NAMES.metal}, ${delivered.minerals} ${RESOURCE_NAMES.minerals}, ${delivered.gas} ${RESOURCE_NAMES.gas}, ${delivered.scrap} ${RESOURCE_NAMES.scrap} to ${planet.name}.`,
        },
    };
}
/**
 * Station: ships and cargo transfer to the target planet permanently. If
 * the target stopped being the player's own planet mid-flight, the fleet
 * falls back to returning home untouched. Population capacity is NOT
 * checked here — the dispatch-time check is the only gate (§9); once a
 * Station fleet has departed it always lands, even if that overpopulates
 * the target (a deliberate mechanic, see `isPlanetOverpopulated`). Cargo
 * overflow past storage capacity is lost (there is no fleet left to carry
 * it back).
 */
function resolveStation(fleet, ctx) {
    if (!ctx.targetPlanet || !ctx.targetIsOwn) {
        return noEffect(fleet, ctx, "none", "The target is no longer one of your planets — the fleet returns.");
    }
    const { planet: withCargo, delivered } = deliverCargoToPlanet(ctx.targetPlanet, fleet.cargo);
    let planet;
    if (isTeamPlanet(withCargo)) {
        planet = withCargo;
        for (const [key, count] of Object.entries(fleet.ships)) {
            if (!count)
                continue;
            planet = teamPlanetDockShips(planet, ctx.race, Number(key), count);
        }
    }
    else {
        planet = dockFleetShips(withCargo, fleet.ships);
    }
    return {
        fleet: { ...fleet, ships: {}, cargo: { ...EMPTY_CARGO } },
        targetPlanet: planet,
        report: {
            effect: "stationed",
            resources: delivered,
            message: `Fleet stationed at ${planet.name}.`,
        },
    };
}
/**
 * Colonize: found a new planet at empty coordinates, consuming one Colony
 * Ship (ID 10). The dispatch-time free-coordinate check is only
 * best-effort — the coordinates may have been taken mid-flight, in which
 * case everything turns around. The caller creates the actual planet
 * document from `foundColony` (creating documents is I/O).
 */
function resolveColonize(fleet, ctx) {
    if (ctx.targetPlanet) {
        return {
            fleet,
            targetPlanet: ctx.targetPlanet,
            report: {
                effect: "colonizeFailed",
                message: "The target coordinates were already colonized — the fleet returns.",
            },
        };
    }
    const colonyShips = fleet.ships[COLONY_SHIP_ID] ?? 0;
    if (colonyShips < 1) {
        // Defensive: dispatch validation requires one, so this indicates a corrupted doc.
        return noEffect(fleet, ctx, "none", "No colony ship on board — colonization aborted.");
    }
    const remaining = { ...fleet.ships, [COLONY_SHIP_ID]: colonyShips - 1 };
    return {
        fleet: { ...fleet, ships: remaining, cargo: { ...EMPTY_CARGO } },
        targetPlanet: null,
        report: {
            effect: "colonized",
            resources: { ...fleet.cargo },
            message: `New colony founded at ${fleet.target.galaxy}:${fleet.target.system}:${fleet.target.position}.`,
        },
        foundColony: { coordinates: fleet.target, cargo: { ...fleet.cargo } },
    };
}
/**
 * Recycle: fill the fleet's cargo hold from the debris field sitting at the
 * target coordinates (#90) — battle wreckage tracked independently of any
 * planet, so this reads `ctx.debrisScrap`, never a planet's own resources.
 * `targetPlanet` is untouched either way: recycling never takes from (or
 * requires) a planet's banked stock, only from the coordinate's debris.
 */
function resolveRecycle(fleet, ctx) {
    const capacity = calculateFleetCargoCapacity(ctx.race, fleet.ships, ctx.shipUpgrades);
    const available = Math.floor(ctx.debrisScrap ?? 0);
    const collected = Math.max(0, Math.min(capacity, available));
    if (collected === 0) {
        return noEffect(fleet, ctx, "none", `No ${RESOURCE_NAMES.scrap} found at the target coordinates.`);
    }
    return {
        fleet: { ...fleet, cargo: { ...EMPTY_CARGO, scrap: collected } },
        targetPlanet: ctx.targetPlanet,
        debrisField: { coordinates: fleet.target, scrap: available - collected },
        report: {
            effect: "recycled",
            resources: { ...EMPTY_CARGO, scrap: collected },
            message: `Collected ${collected} ${RESOURCE_NAMES.scrap}.`,
        },
    };
}
/**
 * Asteroid mining: the roll-based yield fills a share of the fleet's cargo
 * hold determined by the injected roll — `total = round(capacity × (0.3 +
 * 0.4 × roll))`, split 40% metal / 30% minerals / 20% gas / remainder scrap.
 * Deterministic for a given roll, per this package's purity rules.
 *
 * On top of that, any scrap this SPECIFIC asteroid has absorbed from debris
 * fields it drifted over (#90 follow-up, `ctx.asteroidCargoScrap`) is added
 * to the scrap share too, capped by whatever cargo room the roll-based yield
 * left (`capacity − total`) — asteroid mining still can't exceed the fleet's
 * total cargo capacity. Whatever cargo doesn't fit stays with the asteroid
 * (`asteroidCargoField`) for a future harvest.
 */
function resolveHarvestAsteroid(fleet, ctx) {
    if (ctx.asteroidPresent === false) {
        return noEffect(fleet, ctx, "none", "No asteroid at the target coordinates.");
    }
    const capacity = calculateFleetCargoCapacity(ctx.race, fleet.ships, ctx.shipUpgrades);
    const roll = Math.min(1, Math.max(0, ctx.asteroidRoll ?? 0.5));
    const total = Math.round(capacity * (0.3 + 0.4 * roll));
    const metal = Math.round(total * 0.4);
    const minerals = Math.round(total * 0.3);
    const gas = Math.round(total * 0.2);
    const rollScrap = Math.max(0, total - metal - minerals - gas);
    const availableCargo = Math.floor(ctx.asteroidCargoScrap ?? 0);
    const cargoCollected = Math.max(0, Math.min(capacity - total, availableCargo));
    const scrap = rollScrap + cargoCollected;
    const cargo = { metal, minerals, gas, scrap };
    return {
        fleet: { ...fleet, cargo },
        targetPlanet: ctx.targetPlanet,
        ...(ctx.asteroidId && availableCargo > 0
            ? { asteroidCargoField: { asteroidId: ctx.asteroidId, scrap: availableCargo - cargoCollected } }
            : {}),
        report: {
            effect: "harvested",
            resources: cargo,
            message: cargoCollected > 0
                ? `Harvested ${total} resources from the asteroid, plus ${cargoCollected} ${RESOURCE_NAMES.scrap} it had picked up.`
                : `Harvested ${total} resources from the asteroid.`,
        },
    };
}
/**
 * Espionage: roll a report tier from both players' Intelligence science gap
 * (`pickSpyReportTier`) and capture a snapshot of the target planet gated
 * to that tier (`buildSpyReportFields`). A `"none"` roll produces no
 * `SpyReport` at all — see `packages/game-engine/src/espionage.ts`.
 */
function resolveEspionage(fleet, ctx) {
    if (!ctx.targetPlanet) {
        return noEffect(fleet, ctx, "none", "No planet at the target coordinates — nothing to spy on.");
    }
    const target = ctx.targetPlanet;
    const diff = (ctx.intelligenceLevel ?? 0) - (ctx.defenderSciences?.intelligence ?? 0);
    const tier = pickSpyReportTier(diff, ctx.spyReportRoll ?? 0.5);
    const fields = buildSpyReportFields(target, tier);
    if (!fields) {
        return noEffect(fleet, ctx, "espionageFailed", `Spy ship returned from ${target.name} with no usable intel.`);
    }
    const spyReport = {
        fleetId: fleet.id,
        target: fleet.target,
        targetPlanetName: target.name,
        targetUserId: target.userId,
        capturedAt: ctx.now,
        tier,
        ...fields,
    };
    return {
        fleet,
        targetPlanet: ctx.targetPlanet,
        report: { effect: "espionage", message: `Spy report captured from ${target.name} (${tier} intel).` },
        spyReport,
    };
}
/**
 * Attack/Pirate hand-off to the battle engine (#18). Resolves combat via
 * `resolveBattleSequential` (the shipped back-and-forth model — see
 * `battleSequential.ts`; the simultaneous `resolveBattle` in `battle.ts` is
 * kept as a dev-only comparison, wired only into the Battle Simulator page),
 * applies survivors to both sides, and — only on an outright attacker win —
 * loots a race-dependent share of the defender's metal/minerals/gas (Humans
 * 66.6%, everyone else 50%), capped by the attacker's surviving cargo
 * capacity and the defender's actual stock. Scrap is never looted as plunder
 * — it forms a debris field AT THE COORDINATES instead (#90), win, lose, or
 * draw, independent of the defending planet's own resources, so ANY player's
 * recycling ship (attacker, defender, or a bystander) can fly there and collect it
 * later via `resolveRecycle`, not just the planet's occupant.
 */
function resolveBattleMission(fleet, ctx) {
    if (!ctx.targetPlanet) {
        return noEffect(fleet, ctx, "none", "No planet at the target coordinates — the fleet returns without engaging.");
    }
    const rounds = fleet.battleRounds ?? DEFAULT_BATTLE_ROUNDS;
    const missionDef = FLEET_MISSION_DEFINITIONS[fleet.mission];
    const allDefenderShips = ctx.targetPlanet.ships ?? {};
    let combatDefenderShips;
    let bystanderShips;
    if (missionDef.defenderOnlyShipIds) {
        const eligible = new Set(missionDef.defenderOnlyShipIds);
        combatDefenderShips = {};
        bystanderShips = {};
        for (const [key, count] of Object.entries(allDefenderShips)) {
            const id = Number(key);
            if (count && count > 0) {
                if (eligible.has(id)) {
                    combatDefenderShips[id] = count;
                }
                else {
                    bystanderShips[id] = count;
                }
            }
        }
    }
    else {
        combatDefenderShips = allDefenderShips;
        bystanderShips = {};
    }
    const result = resolveBattleSequential({
        attacker: {
            race: ctx.race,
            ships: fleet.ships,
            shipUpgrades: ctx.shipUpgrades,
            sciences: ctx.attackerSciences ?? DEFAULT_SCIENCES,
        },
        defender: {
            race: ctx.defenderRace ?? ctx.race,
            ships: combatDefenderShips,
            shipUpgrades: ctx.defenderShipUpgrades,
            sciences: ctx.defenderSciences ?? DEFAULT_SCIENCES,
        },
        rounds,
    }, ctx.battleRolls ?? []);
    const debrisField = result.scrap > 0
        ? { coordinates: fleet.target, scrap: Math.floor(ctx.debrisScrap ?? 0) + result.scrap }
        : undefined;
    let targetResources = { ...ctx.targetPlanet.resources };
    let stolen = { ...EMPTY_CARGO };
    if (result.outcome === "attackerWins") {
        const capacity = calculateFleetCargoCapacity(ctx.race, result.attackerSurvivors, ctx.shipUpgrades);
        const lootPercent = BATTLE_LOOT_PERCENT[ctx.race];
        const desired = {
            metal: Math.floor(targetResources.metal.current * lootPercent),
            minerals: Math.floor(targetResources.minerals.current * lootPercent),
            gas: Math.floor(targetResources.gas.current * lootPercent),
        };
        const desiredTotal = desired.metal + desired.minerals + desired.gas;
        const scale = desiredTotal > capacity && desiredTotal > 0 ? capacity / desiredTotal : 1;
        stolen = {
            metal: Math.floor(desired.metal * scale),
            minerals: Math.floor(desired.minerals * scale),
            gas: Math.floor(desired.gas * scale),
            scrap: 0,
        };
        targetResources = {
            ...targetResources,
            metal: { ...targetResources.metal, current: targetResources.metal.current - stolen.metal },
            minerals: { ...targetResources.minerals, current: targetResources.minerals.current - stolen.minerals },
            gas: { ...targetResources.gas, current: targetResources.gas.current - stolen.gas },
        };
    }
    // Trade-token reward: pirate-only, and only on a WON raid that both (a)
    // wins the roll and (b) loots enough to clear
    // calculatePirateTokenLootThreshold — a raid that wins but nets a
    // trivial haul (e.g. a near-empty target) earns nothing, even if the
    // roll succeeds. An attack mission never rolls (fleet.mission ===
    // "pirate" gates it), and a lost or drawn pirate raid never rolls
    // either (nested inside the attackerWins branch above's condition,
    // re-checked here since the loot block above is itself gated on the
    // same outcome — `stolen` is the empty cargo for any other outcome, so
    // `lootTotal` naturally fails the threshold too). Always exactly 1
    // token when every gate passes — never scaled by loot size or roll.
    const lootTotal = stolen.metal + stolen.minerals + stolen.gas;
    const tradeTokensGranted = fleet.mission === "pirate" &&
        result.outcome === "attackerWins" &&
        (ctx.pirateTokenRoll ?? 1) < PIRATE_TRADE_TOKEN_CHANCE &&
        lootTotal >= calculatePirateTokenLootThreshold(ctx.attackerResourcePoints ?? 0)
        ? 1
        : 0;
    const survivingDefenderShips = { ...bystanderShips, ...result.defenderSurvivors };
    const targetPlanet = {
        ...ctx.targetPlanet,
        ships: survivingDefenderShips,
        resources: targetResources,
    };
    const effect = result.outcome === "attackerWins" ? "battleWon" :
        result.outcome === "defenderWins" ? "battleLost" :
            "battleDraw";
    const message = result.outcome === "attackerWins"
        ? `Victory at ${targetPlanet.name} — the defending fleet was destroyed.`
        : result.outcome === "defenderWins"
            ? `Defeat at ${targetPlanet.name} — the attacking fleet was destroyed.`
            : `The battle at ${targetPlanet.name} ended in a draw after ${rounds} rounds.`;
    return {
        fleet: { ...fleet, ships: result.attackerSurvivors },
        targetPlanet,
        debrisField,
        report: { effect, message, resources: stolen },
        battleResult: result,
        tradeTokensGranted,
    };
}
/**
 * The defender's OWN copy of a battle's outcome (#18/#19 follow-up) — the
 * flip side of {@link resolveBattleMission}'s `effect`/`message`, which are
 * always framed from the attacker's perspective ("Victory"/"Defeat" describe
 * the attacker). Without this, a defender who was actually crushed sees
 * "Victory at <their own planet> — the defending fleet was destroyed" in
 * their own flight log, reading as if THEY won.
 */
export function defenderArrivalReport(outcome, targetPlanetName, rounds) {
    if (outcome === "attackerWins") {
        return {
            effect: "battleLost",
            message: `Defeat at ${targetPlanetName} — your fleet was destroyed defending against the attack.`,
        };
    }
    if (outcome === "defenderWins") {
        return {
            effect: "battleWon",
            message: `Victory at ${targetPlanetName} — the attacking fleet was destroyed.`,
        };
    }
    return {
        effect: "battleDraw",
        message: `The battle at ${targetPlanetName} ended in a draw after ${rounds} rounds.`,
    };
}
/**
 * Land a returning fleet back home: dock its ships (releasing them from
 * the `shipsInFlight` ledger) and unload its cargo into the origin planet.
 * Metal/minerals/gas beyond storage capacity are lost; scrap is uncapped.
 * The gas paid at dispatch is never refunded.
 */
export function resolveFleetReturn(fleet, originPlanet, now) {
    if (fleet.status !== "returning") {
        throw new QueueError("failed-precondition", `Fleet ${fleet.id} is not returning`);
    }
    let docked;
    if (isTeamPlanet(originPlanet) && fleet.originRace) {
        docked = teamPlanetReturnShips(originPlanet, fleet.originRace, fleet.ships);
    }
    else {
        docked = releaseShipsInFlight(dockFleetShips(originPlanet, fleet.ships), fleet.ships);
    }
    const { planet, delivered } = deliverCargoToPlanet(docked, fleet.cargo);
    return {
        fleet: {
            ...fleet,
            status: "completed",
            cargo: { ...EMPTY_CARGO },
            updatedAt: now,
        },
        planet,
        delivered: isEmptyCargo(fleet.cargo) ? { ...EMPTY_CARGO } : delivered,
    };
}
// ---------------------------------------------------------------------------
// Recall (§3.1)
// ---------------------------------------------------------------------------
/**
 * Recall a fleet still travelling to its destination: it turns around
 * immediately, and the trip home takes exactly as long as it has been
 * flying out. Rejects once the fleet has arrived (`arrivesAt <= now`) —
 * "if a flight has already arrived at its destination, it cannot be
 * returned manually anymore" — UNLESS the mission has a `loiterSeconds`
 * dwell period (Espionage, Sun Support), in which case recall also works
 * while the fleet sits at the target, starting its journey home
 * immediately (taking its normal one-way transit time). Gas is not
 * reimbursed either way.
 */
export function recallFleet(fleet, now) {
    if (fleet.status === "outbound") {
        if (fleet.arrivesAt.getTime() <= now.getTime()) {
            throw new QueueError("failed-precondition", "The fleet has already reached its destination and cannot be recalled");
        }
        const returnSeconds = calculateRecallReturnSeconds(fleet.departedAt, now);
        return {
            ...fleet,
            status: "returning",
            recalled: true,
            returnsAt: new Date(now.getTime() + returnSeconds * 1000),
            updatedAt: now,
        };
    }
    const loiters = FLEET_MISSION_DEFINITIONS[fleet.mission].loiterSeconds !== undefined;
    if (fleet.status === "returning" && loiters && !fleet.recalled && fleet.returnsAt.getTime() > now.getTime()) {
        // Sun Support has no fixed loiter window to cut short — recalling it
        // starts the SAME flat 24h return trip a resolved battle would
        // (2026-08-14 gameplay ruling: "if the attack is recalled OR the
        // battle occurs, sun supporters start their 24h return countdown"),
        // not "however long the outbound trip took" like Espionage.
        const returnDelaySeconds = fleet.mission === "sunDefence"
            ? SUN_DEFENCE_RETURN_HOLD_SECONDS
            : fleet.oneWaySeconds;
        return {
            ...fleet,
            recalled: true,
            returnsAt: new Date(now.getTime() + returnDelaySeconds * 1000),
            updatedAt: now,
        };
    }
    throw new QueueError("failed-precondition", "Only an outbound fleet can be recalled");
}
/**
 * Turn an in-flight Colonize fleet around because its target coordinate became
 * occupied *before* it arrived — the flight-rules requirement that an active
 * colonize flight be re-checked every tick, not only at arrival (arrival-time
 * occupancy is already handled by {@link resolveColonize}'s `colonizeFailed`).
 * Same turn-around math as a recall: the trip home takes exactly as long as it
 * has been flying out, gas stays spent. Only valid for an outbound colonize
 * fleet that has not yet reached its destination — once `arrivesAt <= now` the
 * normal arrival path resolves it instead.
 */
export function turnBackColonizeFleet(fleet, now) {
    if (fleet.mission !== "colonize" || fleet.status !== "outbound") {
        throw new QueueError("failed-precondition", "Only an outbound colonize fleet can be turned back");
    }
    if (fleet.arrivesAt.getTime() <= now.getTime()) {
        throw new QueueError("failed-precondition", "The colonize fleet has already reached its destination");
    }
    const returnSeconds = calculateRecallReturnSeconds(fleet.departedAt, now);
    return {
        ...fleet,
        status: "returning",
        recalled: true,
        returnsAt: new Date(now.getTime() + returnSeconds * 1000),
        arrivalReport: {
            effect: "colonizeFailed",
            message: "The target coordinates were colonized by someone else — the fleet is returning.",
        },
        updatedAt: now,
    };
}
//# sourceMappingURL=fleetResolution.js.map